import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import {
  Box,
  Button, CircularProgress,
  Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Paper, TextField,
  Typography,
} from "@mui/material";
import useSWR from "swr";
import { swrKey, userFetcher } from "components/userApi";
import UserEditComponent from "./UserEditComponent";
import ImageEditComponent from "./ImageEditComponent";
import EmailEditComponent from "./EmailEditComponent";
import { useDeleteUser } from "./deleteUser";

export default function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data, isLoading, error, mutate } = useSWR([swrKey, userId], userFetcher);
  const { deleteUser, isDeleting, error: deleteError } = useDeleteUser();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Handle back to list
  const handleBackToList = () => {
    // Get the returnPage parameter from the URL
    const returnPage = searchParams.get("returnPage");
    if (returnPage) {
      // Navigate back to the list with the page parameter
      navigate(`/users?page=${returnPage}`);
    } else {
      // If no returnPage parameter, just navigate back to the list
      navigate("/users");
    }
  };

  const refreshUser = async () => {
    await mutate();
  }

  // Handle delete button click
  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteConfirmText === "削除" && userId) {
      const { success } = await deleteUser(userId);
      if (success) {
        setOpenDeleteDialog(false);
        navigate("/users");
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {isLoading &&
      <>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            会員詳細
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "center", mt: 4 }}
          >
            <CircularProgress />
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            身分証画像
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "center", mt: 4 }}
          >
            <CircularProgress />
          </Box>
        </Paper>
      </>
      }

      {!isLoading && data && (
        <>
          <UserEditComponent user={data} refresh={refreshUser} />
          <EmailEditComponent user={data} refresh={refreshUser} />
          <ImageEditComponent user={data} refresh={refreshUser} />
        </>
      )}

      {error && (
        <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
          <Typography variant="h6" color="error">
            エラーが発生しました
          </Typography>
          <Typography variant="body2">
            {error.message || "データの取得中に問題が発生しました。"}
          </Typography>
        </Box>
      )}

      {deleteError && (
        <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
          <Typography variant="h6" color="error">
            削除エラーが発生しました
          </Typography>
          <Typography variant="body2">
            {deleteError.message || "ユーザーの削除中に問題が発生しました。"}
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button variant="outlined" onClick={handleBackToList}>
          会員一覧へ戻る
        </Button>
        <Button
          variant="contained"
          color="error"
          sx={{ mr: 2 }}
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? "削除中..." : "会員を削除"}
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">会員を削除します。</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            メールアドレス: {data?.email}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            会員番号: {data?.memberNumber}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            この会員によるファクタリング申込も削除されます
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            削除する場合は、下記に削除と記入してください。
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            color="primary"
            disabled={isDeleting}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            disabled={deleteConfirmText !== "削除" || isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : "削除"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
